import { Modal } from "components";
import { Loading } from "components/panels";
import { Button } from "components/Atoms/Button";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import { CreditApi } from "queries/Credit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";

interface Props {
  onClose: () => void;
}

export const AutoReloadModal = (props: Props) => {
  if (!site.permissions.canConfigureSystemSettings) {
    return <Modal
      isOpen
      handleClose={props.onClose}
      title={RESX.Credit.ConfigureAutoReloadSettings}
      width="tiny"
    >
      <div className="ui message red">You need to have system admin permission.</div>
    </Modal>;
  }

  return <AutoReloadModalInner {...props} />;
};

const AutoReloadModalInner = (props: Props) => {
  const clientId = site.currentEnvironment.client.clientId;
  const creditAccountQuery = CreditApi.GetCreditAccountByClient.useQuery({ clientId }, {});

  return <Modal
    isOpen
    handleClose={props.onClose}
    title={RESX.Credit.ConfigureAutoReloadSettings}
    width="tiny"
  >
    {creditAccountQuery.data == null
      ? <Loading />
      : <AutoReloadForm
        creditAccount={creditAccountQuery.data.data}
        clientId={clientId}
        onClose={props.onClose}
      />}
  </Modal>;
};

interface FormProps {
  creditAccount: server.dto.CreditAccount;
  clientId: number;
  onClose: () => void;
}

interface FormState {
  enabled: boolean;
  triggerAmount: number;
  goalAmount: number;
}

const AutoReloadForm = (props: FormProps) => {
  const [formState, setFormState] = React.useState<FormState>({
    enabled: true,
    triggerAmount: props.creditAccount.autoReloadTriggerAmount ?? serverReference.constants.credit.defaultAutoReloadTriggerAmount,
    goalAmount: props.creditAccount.autoReloadGoalAmount ?? serverReference.constants.credit.defaultAutoReloadGoalAmount,
  });

  const validators = useValidators();

  const queryClient = useQueryClient();

  const minDiff = serverReference.constants.credit.minAutoReloadTriggerGoalDifference;
  const minTrigger = serverReference.constants.credit.minAutoReloadTriggerAmount;
  const maxTrigger = serverReference.constants.credit.maxAutoReloadTriggerAmount;
  const minGoal = minTrigger + minDiff;
  const maxGoal = serverReference.constants.credit.maxAutoReloadGoalAmount;

  const onTriggerChange = (newTrigger: number) => setFormState(prev => {
    const nextGoal = Math.max(prev.goalAmount, newTrigger + minDiff);
    if (newTrigger < minTrigger || newTrigger > maxTrigger || nextGoal > maxGoal) return prev;
    return { ...prev, triggerAmount: newTrigger, goalAmount: nextGoal };
  });

  const onGoalChange = (newGoal: number) => setFormState(prev => {
    const nextTrigger = Math.min(prev.triggerAmount, newGoal - minDiff);
    if (newGoal < minGoal || newGoal > maxGoal || nextTrigger < minTrigger) return prev;
    return { ...prev, goalAmount: newGoal, triggerAmount: nextTrigger };
  });

  const saveMutation = useMutation((dto: server.dto.ConfigAutoReloadCreditsInstruction) =>
    window.CoreApi.Credit.ConfigAutoReload(dto, { toastOnError: true })
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const validatorResult = await validators.validateAll();
    if (validatorResult.hasErrors) {
      return;
    }

    const res = await saveMutation.mutateAsync({
      clientId: props.clientId,
      enabled: formState.enabled,
      triggerAmount: formState.triggerAmount,
      goalAmount: formState.goalAmount,
    });

    if (!res.data.hasErrors) {
      queryClient.invalidateQueries(CreditApi.GetCreditAccountByClient.getCacheKey({ clientId: props.clientId }));
      props.onClose();
    }
  };

  return <ValidatorContext settings={validators.validatorSettings}>
    <form className="flex-column gap-big" onSubmit={onSubmit}>
      <div className="flex gap-large align-center">
        <div className="grow1">
          <h4>{RESX.Credit.AutoReloadCredits}</h4>
          <div>Avoid service disruptions by auto-reloading credits when your balance reaches a specified minimum.</div>
        </div>
        <Checkbox
          checked={formState.enabled}
          onChange={(_, checked) => setFormState(prev => ({ ...prev, enabled: checked }))}
        />
      </div>

      {formState.enabled && <div className="flex gap">
        <div className="grow1 flex-column gap-small">
          <div>{RESX.Credit.MinimumCreditBalance}</div>
          <RequiredNumberInput
            value={formState.triggerAmount}
            onChange={onTriggerChange}
            minValue={minTrigger}
            maxValue={maxTrigger}
            decimalPoints={0}
            canBeEmpty={false}
            canBeNegative={false}
            validatorKey="triggerAmount"
            labelContent="EUR"
          />
        </div>
        <div className="grow1 flex-column gap-small">
          <div>Bring credit balance back up to</div>
          <RequiredNumberInput
            value={formState.goalAmount}
            onChange={onGoalChange}
            minValue={minGoal}
            maxValue={maxGoal}
            decimalPoints={0}
            canBeEmpty={false}
            canBeNegative={false}
            validatorKey="goalAmount"
            labelContent="EUR"
          />
        </div>
      </div>}

      <div className="flex justify-end">
        <Button
          type="primary"
          htmlType="submit"
          loading={saveMutation.isLoading}
          disabled={saveMutation.isLoading}
          label={RESX.Buttons.btnSave}
        />
      </div>
    </form>
  </ValidatorContext>;
};
